import { AssessmentCreateSchema, AssessmentResponse, AssessmentStatus, AssessmentUpdateSchema, CollectionResponse, FileResponse, Response } from '@opp/shared';
import { apiRequest } from './api';
import { storage } from './storageService';

export async function createAssessment(data: AssessmentCreateSchema) {
  return apiRequest<{ data: AssessmentResponse }>('/assessment', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAssessment(id: string) {
  return apiRequest<Response<AssessmentResponse>>(`/assessment/${id}`, {
    method: 'GET',
  });
}

export async function listAssessments(
  status: AssessmentStatus | 'ALL' | undefined,
  organizationId: string,
  page: number = 1,
  perPage: number = 10,
  storeId?: string,
  filters?: {
    patientId?: string;
    patientName?: string;
    patientDocument?: string; // compat
    patientPhone?: string;
    responsibleId?: string;
    classificationIds?: string[];
    search?: string;
  },
  sort?: {
    orderBy?: 'DATE' | 'CREATED' | 'UPDATED' | 'NAME';
    orderDirection?: 'asc' | 'desc';
  },
) {
  const searchParams = new URLSearchParams({
    organizationId,
    page: String(page),
    perPage: String(perPage),
  });
  if (status && status !== 'ALL') {
    searchParams.set('status', status);
  }
  if (storeId) {
    searchParams.set('storeId', storeId);
  }
  if (filters?.patientId) {
    searchParams.set('patientId', filters.patientId);
  }
  if (filters?.patientName?.trim()) {
    searchParams.set('patientName', filters.patientName.trim());
  }
  if (filters?.patientPhone?.trim()) {
    searchParams.set('patientPhone', filters.patientPhone.trim());
  } else if (filters?.patientDocument?.trim()) {
    searchParams.set('patientDocument', filters.patientDocument.trim());
  }
  if (filters?.responsibleId) {
    // Backend usa `userId` como filtro do dentista responsável pelo atendimento
    searchParams.set('userId', filters.responsibleId);
  }
  if (filters?.classificationIds?.length) {
    // Repetido: ?classificationIds=a&classificationIds=b
    filters.classificationIds.forEach((id) => searchParams.append('classificationIds', id));
  }
  if (filters?.search?.trim()) {
    searchParams.set('search', filters.search.trim());
  }
  if (sort?.orderBy) {
    searchParams.set('orderBy', sort.orderBy);
  }
  if (sort?.orderDirection) {
    searchParams.set('orderDirection', sort.orderDirection);
  }
  return apiRequest<CollectionResponse<AssessmentResponse>>(`/assessment?${searchParams}`, {
    method: 'GET',
  });
}

export async function updateAssessment(id: string, data: AssessmentUpdateSchema) {
  return apiRequest<Response<void>>(`/assessment/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}


export async function deleteAssessment(id: string) {
  return apiRequest<Response<void>>(`/assessment/${id}`, {
    method: 'DELETE',
  });
}

export interface AssessmentFileUpdate {
  key: string;
  value: Array<{
    file: string;
    original: boolean;
  }>;
}

export async function updateAssessmentFiles(id: string, data: AssessmentFileUpdate) {
  return apiRequest<Response<FileResponse[]>>(`/assessment/${id}/files`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function createTreatments(assessmentId: string, data: Array<{
  name: string;
  description?: string;
  order?: number;
  heldIn?: Date;
  obs?: string;
}>) {
  return apiRequest<Response<void>>(`/assessment/${assessmentId}/treatments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTreatment(
  assessmentId: string,
  treatmentId: string,
  data: { name: string; description?: string }
) {
  return apiRequest<Response<void>>(`/assessment/${assessmentId}/treatments/${treatmentId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteTreatment(assessmentId: string, treatmentId: string) {
  return apiRequest<Response<void>>(`/assessment/${assessmentId}/treatments/${treatmentId}`, {
    method: 'DELETE',
  });
}

export async function updateTreatmentOrders(assessmentId: string, updates: Array<{ id: string; order: number }>) {
  return apiRequest<Response<void>>(`/assessment/${assessmentId}/treatments/orders`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function updateAssessmentPresentation(assessmentId: string, presentationEnabled: boolean) {
  return apiRequest<Response<void>>(`/assessment/${assessmentId}/presentation`, {
    method: 'PATCH',
    body: JSON.stringify({ presentationEnabled }),
  });
}

export async function updateAssessmentFilePresentation(
  assessmentId: string,
  key: string,
  presentationVisible: boolean,
) {
  return apiRequest<Response<void>>(`/assessment/${assessmentId}/files/presentation`, {
    method: 'PATCH',
    body: JSON.stringify({ key, presentationVisible }),
  });
}

// Função pública para buscar apresentação (não requer autenticação)
export async function getPresentation(id: string) {
  const API = import.meta.env.VITE_API_URL;
  const response = await fetch(`${API}/assessment/${id}/presentation`, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || 'Erro ao buscar apresentação');
  }

  return data as Response<AssessmentResponse>;
}

/** PDF gerado no servidor (Chromium/Puppeteer). 501 = funcionalidade desativada — usar fallback no cliente. */
export async function requestPresentationPdfBlob(
  assessmentId: string,
  options?: { signal?: AbortSignal },
): Promise<Blob> {
  const API = import.meta.env.VITE_API_URL;
  const token = storage.get<string>('token');
  if (!token) {
    throw new Error('Sessão expirada');
  }

  const res = await fetch(`${API}/assessment/${assessmentId}/pdf`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
    mode: 'cors',
    signal: options?.signal,
  });

  if (res.status === 501) {
    const e = new Error('PDF_RENDER_DISABLED');
    (e as Error & { status: number }).status = 501;
    throw e;
  }

  if (!res.ok) {
    let msg = 'Erro ao gerar PDF';
    try {
      const j = (await res.json()) as { message?: string };
      if (typeof j.message === 'string') msg = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }

  return res.blob();
}
